import React from "react";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import { Box } from "@material-ui/core";
import ModalSize from "./ModalSize";
import ButtonWithLoading from "../Loading/ButtonWithLoading";
import LoadingButton from "../Loading/LoadingButton";
import CloseIcon from "@material-ui/icons/Close";

const styles = () =>
  createStyles({
    root: {
      margin: "25px",
      padding: "0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    closeButton: {},
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  size: ModalSize;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, size, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <div style={{ fontWeight: "bold", fontSize: size === ModalSize.fullScreen ? "20px" : "18px" }}>{children}</div>
      {onClose ? (
        <IconButton aria-label="close" style={{ padding: "0" }} className={classes.closeButton} onClick={onClose}>
          <CloseIcon fontSize="large" aria-label="close" />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    overflowY: "auto",
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const modalWidth = (width: ModalSize): number | string => {
  switch (width) {
    case ModalSize.large:
      return 500;
    case ModalSize.medium:
      return 350;
    case ModalSize.small:
      return 300;
    case ModalSize.fullScreen:
      return "100%";
    default:
      return 250;
  }
};

type StandardModalProps = {
  title: React.ReactNode;
  open: boolean;
  size: ModalSize;
  //pass in action and onclose functions
  cancelClose?: () => void;
  onSubmit?: () => void;
  onlyOneButton?: boolean;
  noButtons?: boolean;
  closeButtonText?: string;
  submitButtonText?: string;
  // is loadingButton being used?
  loadingButtonState?: LoadingButton;
  changeLoadingButtonState?: (loadingState: LoadingButton) => void;
  children: React.ReactNode;
  className?: string;
};

const noop = () => {};

const StandardModal = ({
  title,
  open,
  size,
  cancelClose,
  onSubmit,
  onlyOneButton,
  noButtons,
  closeButtonText,
  submitButtonText,
  loadingButtonState,
  changeLoadingButtonState,
  children,
  className,
}: StandardModalProps) => (
  <div>
    <Dialog
      aria-labelledby="customized-dialog-title"
      open={open}
      onClose={cancelClose}
      className={className}
      fullScreen={size === ModalSize.fullScreen}
    >
      <Box
        width={modalWidth(size)}
        onKeyPress={event => {
          if (event.key === "Enter" && onSubmit) {
            onSubmit();
          }
        }}
      >
        <DialogTitle id="customized-dialog-title" onClose={cancelClose || noop} size={size}>
          {title}
        </DialogTitle>
        <DialogContent
          style={{ whiteSpace: "pre-wrap", maxHeight: size === ModalSize.fullScreen ? "none" : "350px" }}
          dividers
          className={className}
        >
          {children}
        </DialogContent>
        {noButtons || (
          <DialogActions>
            {cancelClose && !onlyOneButton && (
              <Button onClick={cancelClose}>{closeButtonText ? closeButtonText : "Cancel"}</Button>
            )}
            {loadingButtonState !== undefined && onSubmit && changeLoadingButtonState ? (
              <ButtonWithLoading
                autoFocus
                color="primary"
                buttonClickText={submitButtonText ? submitButtonText : "Ok"}
                buttonFinishedText="Success!"
                clickHandler={onSubmit}
                loadingButtonState={loadingButtonState}
                changeLoadingButtonState={changeLoadingButtonState}
              />
            ) : (
              <Button autoFocus color="primary" onClick={onSubmit}>
                {submitButtonText ? submitButtonText : "Ok"}
              </Button>
            )}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  </div>
);

export default StandardModal;
